import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { userServiceAPI } from "../services/api";
import { getStoredToken } from "../utils/tokenUtils";
import { API } from "../config/api.paths";

const DeviceAuthApprovalPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const requestId = searchParams.get("request_id");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState("");
  const [actionMessage, setActionMessage] = useState("");

  const isLoggedIn = useMemo(() => !!getStoredToken(), []);

  const loadDetails = async () => {
    if (!requestId) {
      setError("Missing device authentication request id.");
      setLoading(false);
      return;
    }
    try {
      const response = await userServiceAPI.get(API.user.auth.device.details(requestId));
      setDetails(response.data?.data || null);
    } catch (err) {
      setError(err.response?.data?.message || "Unable to load request details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDetails();
  }, [requestId]);

  const handleDecision = async (approve) => {
    if (!requestId) return;
    setSubmitting(true);
    setError("");
    try {
      const endpoint = approve
        ? API.user.auth.device.approve(requestId)
        : API.user.auth.device.reject(requestId);
      const response = await userServiceAPI.post(endpoint, approve ? {} : { reason: "Rejected by user" });
      const status = response.data?.data?.status;
      if (status === "approved") {
        setActionMessage("Request approved. You can return to the desktop app.");
      } else if (status === "rejected") {
        setActionMessage("Request rejected.");
      } else {
        setActionMessage("Request updated.");
      }
      await loadDetails();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update this request.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading request...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow p-6 space-y-4">
        <h1 className="text-xl font-semibold text-gray-900">Web Authentication</h1>
        {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 p-3 rounded">{error}</div>}
        {actionMessage && (
          <div className="text-sm text-green-700 bg-green-50 border border-green-200 p-3 rounded">{actionMessage}</div>
        )}

        {details && (
          <div className="text-sm text-gray-700 space-y-1">
            <div><strong>From:</strong> {details.app_name || "Desktop App"}</div>
            <div><strong>Source:</strong> {details.source || "desktop-app"}</div>
            <div><strong>Device:</strong> {details.device_name || "Unknown Device"}</div>
            <div><strong>Status:</strong> {details.status}</div>
          </div>
        )}

        {!isLoggedIn ? (
          <Button
            className="w-full"
            onClick={() =>
              navigate(`/login?redirect=${encodeURIComponent(`/device-auth/approve?request_id=${requestId || ""}`)}`)
            }
          >
            Login To Continue
          </Button>
        ) : details?.status === "pending" ? (
          <div className="flex gap-3">
            <Button className="flex-1" disabled={submitting} onClick={() => handleDecision(true)}>
              {submitting ? "Please wait..." : "Approve"}
            </Button>
            <Button className="flex-1" variant="outline" disabled={submitting} onClick={() => handleDecision(false)}>
              Reject
            </Button>
          </div>
        ) : (
          <div className="text-sm text-gray-600">
            Request is <strong>{details?.status || "unknown"}</strong>.
          </div>
        )}

        <div className="text-xs text-gray-500">
          Need help? <Link className="underline" to="/support">Support</Link>
        </div>
      </div>
    </div>
  );
};

export default DeviceAuthApprovalPage;
